import { useCallback } from 'react';
import { updateGridDimensionConfig } from '../grid/reduxStore/editorSlice.ts';
import { setSaveStatus } from '../grid/reduxStore/saveStatusSlice.ts';
import { useAppDispatch } from '../grid/reduxStore/Store.ts';
import { useCurrentUser } from '../../../providers/UserProvider.tsx';
import { useSocketClient } from '../../../providers/SocketContext.tsx';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus.ts';
import { getRoundedValue } from '../GridDndEditor/gridHelper.tsx';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion.ts';
import { UpdateBlockDimensionCommand } from '../UndoRedo/UpdateBlockDimensionCommand.ts';
import { updateBlockDimensionsViaSocket } from '../grid/reduxStore/saveHandlers.ts';
import type { DimensionPositionStateChangedType } from './UseBlockDimensionChangedHandler.ts';
import type { TableType } from '../grid/reduxStore/table.types.ts';

export type DimensionStateChangedType = {
  widthPx: number;
  heightPx: number;
};

export const ALLOWED_DECIMAL_PLACES_FOR_DIMENSION = 2;

export type BlockDimensionChangedHandlerType = (sectionId: string, blockId: string, blockState: DimensionStateChangedType) => Promise<void>;

/**
 * Hook for changing block dimensions with full undo/redo support
 * Handles both dimension changes and position changes (if block moves during resize)
 */
export function useNewBlockDimensionChangedHandler() {
  const dispatch = useAppDispatch();
  const { data } = useCurrentUser();
  const userId = data.id;
  const { socketClient } = useSocketClient();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useCallback(
    async (
      sectionId: string,
      blockId: string,
      stateBeforeResizing: DimensionPositionStateChangedType,
      stateAfterResizing: DimensionPositionStateChangedType,
      tableContent?: { previousContent: TableType; newContent: TableType }
    ): Promise<void> => {
      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();

      const stateBefore = {
        widthPx: getRoundedValue(stateBeforeResizing.widthPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
        heightPx: getRoundedValue(stateBeforeResizing.heightPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
        xAxisPx: stateBeforeResizing.xAxisPx,
        yAxisPx: stateBeforeResizing.yAxisPx,
      };

      const stateAfter = {
        widthPx: getRoundedValue(stateAfterResizing.widthPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
        heightPx: getRoundedValue(stateAfterResizing.heightPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION),
        xAxisPx: stateAfterResizing.xAxisPx,
        yAxisPx: stateAfterResizing.yAxisPx,
      };

      const command = new UpdateBlockDimensionCommand(
        blockId,
        sectionId,
        stateBefore,
        stateAfter,
        {
          dispatch,
          socketClient,
          userId,
          checkDocumentLockStatus,
        },
        tableContent
      );

      const success = await command.execute();

      if (success) {
        undoRedoManager.pushUndoRedoCommands(command);
      }
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );
}

/**
 * Hook for changing dimensions WITHOUT undo/redo support
 * Used internally by commands
 */
export function useNewBlockDimensionChangedHandlerWithoutUndoRedo(): BlockDimensionChangedHandlerType {
  const dispatch = useAppDispatch();
  const { data } = useCurrentUser();
  const userId = data.id;
  const { socketClient } = useSocketClient();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useCallback(
    async (sectionId: string, blockId: string, { widthPx, heightPx }: DimensionStateChangedType): Promise<void> => {
      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const roundedWidth = getRoundedValue(widthPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION);
      const roundedHeight = getRoundedValue(heightPx, ALLOWED_DECIMAL_PLACES_FOR_DIMENSION);

      dispatch(
        updateGridDimensionConfig({
          sectionId,
          blockId,
          width: roundedWidth,
          height: roundedHeight,
        })
      );

      const data = {
        blockId,
        userId,
        dimensions: {
          width_px: roundedWidth,
          height_px: roundedHeight,
        },
      };
      await updateBlockDimensionsViaSocket(socketClient, data);
      return;
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );
}
